/* eslint-disable no-nested-ternary */
import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import ReactSVG from 'react-svg';
import { Close, MoreVert, GetApp, PlayCircleFilled } from '@material-ui/icons';
import { MessageTypeEnum } from '~/enums/conversations';
import { FileIcon } from '~/assets/svgIcons';
import { log, electron } from '~/helpers';
import AudioPlayer from '../AudioPlayer';
import FilePreview from './FilePreview';
import PdfPreview from './PdfPreview';
import {
  Container,
  ContentWrapper,
  Image,
  VideoThumb,
  VideoThumbWrapper,
  VideoPlayOverlay,
  VideoDurationBadge,
  VideoPreviewFull,
  ImagePreviewBackdrop,
  ImagePreviewFull,
  MediaErrorPlaceholder,
  PdfPreviewFrame,
  ImagePreviewActions,
  ImagePreviewIconButton,
  ImagePreviewMenu,
  ImagePreviewMenuItem,
  // Divider,
} from './styles';

interface FileThumbsProps {
  file: {
    name: string;
    type: MessageTypeEnum;
    size: number;
    url: string | null | undefined;
    thumbnailUrl?: string | null;
    contentType?: string;
  };
}

const formatVideoDuration = (seconds: number): string => {
  if (!Number.isFinite(seconds) || seconds < 0) return '';
  const total = Math.floor(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return h > 0 ? `${pad(h)}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
};

const FileThumbs: React.FC<FileThumbsProps> = ({ file }) => {
  const [isMediaExpanded, setIsMediaExpanded] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [videoDuration, setVideoDuration] = useState<number | null>(null);
  const [hasImageError, setHasImageError] = useState(false);
  const [hasVideoError, setHasVideoError] = useState(false);

  const isAudio =
    file.contentType?.startsWith('audio/') ||
    file.type === MessageTypeEnum.AUDIO;

  const isImage =
    (file.type === MessageTypeEnum.IMAGE ||
      file.type === MessageTypeEnum.STICKER) &&
    !hasImageError;

  const isPreviewableVideo =
    file.type === MessageTypeEnum.VIDEO && !hasVideoError;

  const isPdf =
    file.contentType === 'application/pdf' ||
    file.name?.toLowerCase().endsWith('.pdf');

  const canShowPreview = isImage || isPreviewableVideo || isPdf;

  useEffect(() => {
    if (!isMediaExpanded) {
      setIsMenuOpen(false);
      return undefined;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsMediaExpanded(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isMediaExpanded]);

  const handleDownloadMedia = async (event: React.MouseEvent) => {
    if (!file.url) return;
    event.stopPropagation();
    setIsMenuOpen(false);

    // In Electron, delegate to the main process so the native download
    // manager (with Save-As dialog) handles it — avoids renderer CORS.
    if (window.isElectron) {
      electron.downloadFile({ url: file.url, filename: file.name });
      return;
    }

    const triggerLink = (href: string, openInNewTab: boolean) => {
      const link = document.createElement('a');
      link.href = href;
      link.download = file.name || 'download';
      if (openInNewTab) {
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
      }
      document.body.appendChild(link);
      link.click();
      link.remove();
    };

    try {
      const response = await fetch(file.url);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      triggerLink(blobUrl, false);
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      // Cross-origin media without CORS headers — fall back to a direct
      // anchor. The browser will download if the server sends
      // Content-Disposition: attachment, otherwise it opens the media
      // in a new tab so the user can still save it manually.
      log.warn(
        'Media blob fetch blocked (likely CORS). Falling back to direct link.',
        err
      );
      triggerLink(file.url, true);
    }
  };

  return (
    <Container>
      <ContentWrapper>
        {!file.url ? (
          <MediaErrorPlaceholder>
            <ReactSVG src={FileIcon} className="icon-file" />
            <span>{file.name}</span>
            File could not be loaded
          </MediaErrorPlaceholder>
        ) : isAudio ? (
          <AudioPlayer url={file.url} />
        ) : file.type === MessageTypeEnum.IMAGE && hasImageError ? (
          <MediaErrorPlaceholder>
            <ReactSVG src={FileIcon} className="icon-file" />
            File could not be loaded
          </MediaErrorPlaceholder>
        ) : file.type === MessageTypeEnum.VIDEO && hasVideoError ? (
          <MediaErrorPlaceholder>
            <ReactSVG src={FileIcon} className="icon-file" />
            File could not be loaded
          </MediaErrorPlaceholder>
        ) : isPdf ? (
          <PdfPreview
            name={file.name}
            size={file.size}
            onPreview={() => setIsMediaExpanded(true)}
            onDownload={handleDownloadMedia}
          />
        ) : canShowPreview ? (
          <>
            {isImage ? (
              <Image
                src={file?.thumbnailUrl || file.url}
                alt={file.name}
                onError={() => setHasImageError(true)}
                onClick={() => setIsMediaExpanded(true)}
              />
            ) : (
              <VideoThumbWrapper onClick={() => setIsMediaExpanded(true)}>
                <VideoThumb
                  width="220"
                  muted
                  onError={() => setHasVideoError(true)}
                  onLoadedMetadata={(
                    e: React.SyntheticEvent<HTMLVideoElement>
                  ) => {
                    const { duration } = e.currentTarget;
                    if (Number.isFinite(duration)) setVideoDuration(duration);
                  }}>
                  <source src={file.url} type="video/mp4" />
                </VideoThumb>
                <VideoPlayOverlay>
                  <PlayCircleFilled />
                </VideoPlayOverlay>
                {videoDuration !== null && (
                  <VideoDurationBadge>
                    {formatVideoDuration(videoDuration)}
                  </VideoDurationBadge>
                )}
              </VideoThumbWrapper>
            )}
          </>
        ) : (
          <FilePreview
            name={file.name}
            size={file.size}
            contentType={file.contentType || ''}
            onDownload={handleDownloadMedia}
          />
        )}
      </ContentWrapper>
      {/* <Divider /> */}
      {isMediaExpanded &&
        canShowPreview &&
        ReactDOM.createPortal(
          <ImagePreviewBackdrop onClick={() => setIsMediaExpanded(false)}>
            {isImage ? (
              <ImagePreviewFull
                src={file.url}
                alt={file.name}
                onError={() => setHasImageError(true)}
                onClick={(e: React.MouseEvent) => e.stopPropagation()}
              />
            ) : isPdf ? (
              <PdfPreviewFrame
                src={file.url ?? undefined}
                title={file.name}
                onClick={(e: React.MouseEvent) => e.stopPropagation()}
              />
            ) : (
              <VideoPreviewFull
                controls
                autoPlay
                onError={() => setHasVideoError(true)}
                onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                <source src={file.url ?? undefined} type="video/mp4" />
              </VideoPreviewFull>
            )}
            <ImagePreviewActions
              onClick={(e: React.MouseEvent) => e.stopPropagation()}>
              <ImagePreviewIconButton
                type="button"
                aria-label="More options"
                aria-haspopup="menu"
                aria-expanded={isMenuOpen}
                onClick={() => setIsMenuOpen(open => !open)}>
                <MoreVert />
              </ImagePreviewIconButton>
              <ImagePreviewIconButton
                type="button"
                aria-label="Close preview"
                onClick={() => setIsMediaExpanded(false)}>
                <Close />
              </ImagePreviewIconButton>
            </ImagePreviewActions>
            {isMenuOpen && (
              <ImagePreviewMenu
                role="menu"
                onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                <ImagePreviewMenuItem
                  type="button"
                  role="menuitem"
                  onClick={handleDownloadMedia}>
                  <GetApp />
                  Save as...
                </ImagePreviewMenuItem>
              </ImagePreviewMenu>
            )}
          </ImagePreviewBackdrop>,
          document.body
        )}
    </Container>
  );
};

export default FileThumbs;
